import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './context/AuthContext';

// Customer Pages
import Landing from './pages/customer/Landing';
import CustomerAuth from './pages/customer/CustomerAuth';
import VerifyAuth from './pages/customer/VerifyAuth';
import CompleteProfile from './pages/customer/CompleteProfile';
import CustomerDashboard from './pages/customer/Dashboard';
import CustomerServices from './pages/customer/Services';
import CustomerRewards from './pages/customer/Rewards';
import CustomerProfile from './pages/customer/Profile';
import CustomerHistory from './pages/customer/History';

// Shared Pages
import PrivacyPolicy from './pages/PrivacyPolicy';

// Staff Pages
import StaffLogin from './pages/staff/Login';
import StaffDashboard from './pages/staff/Dashboard';
import StaffScanner from './pages/staff/Scanner';
import StaffCustomers from './pages/staff/Customers';

// Admin Pages
import AdminLogin from './pages/admin/Login';
import AdminDashboard from './pages/admin/Dashboard';
import AdminScanner from './pages/admin/Scanner';
import AdminCustomers from './pages/admin/Customers';
import AdminServices from './pages/admin/Services';
import AdminRewards from './pages/admin/Rewards';
import AdminPromotions from './pages/admin/Promotions';
import AdminStaff from './pages/admin/Staff';
import AdminAnalytics from './pages/admin/Analytics';
import AdminNotifications from './pages/admin/Notifications';

// Layouts
import CustomerLayout from './layouts/CustomerLayout';
import StaffLayout from './layouts/StaffLayout';
import AdminLayout from './layouts/AdminLayout';

import './App.css';

// Protected Route Components
const CustomerRoute = ({ children, requireProfile = true }) => {
  const { isAuthenticated, role, loading, user } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!isAuthenticated || role !== 'customer') return <Navigate to="/" replace />;
  
  // Check if profile completion is required and redirect if incomplete
  if (requireProfile && user && user.profile_completed === false) {
    return <Navigate to="/complete-profile" replace />;
  }
  
  return children;
};

// Route for profile completion (allows incomplete profiles)
const ProfileCompletionRoute = ({ children }) => {
  const { isAuthenticated, role, loading, user } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!isAuthenticated || role !== 'customer') return <Navigate to="/" replace />;
  
  // If profile is already complete, redirect to dashboard
  if (user && user.profile_completed === true) {
    return <Navigate to="/customer/dashboard" replace />;
  }
  
  return children;
};

const StaffRoute = ({ children }) => {
  const { isAuthenticated, role, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!isAuthenticated || !['staff', 'admin'].includes(role)) return <Navigate to="/staff/login" replace />;
  return children;
};

const AdminRoute = ({ children }) => {
  const { isAuthenticated, role, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!isAuthenticated || role !== 'admin') return <Navigate to="/admin/login" replace />;
  return children;
};

const LoadingScreen = () => (
  <div className="min-h-screen bg-[#FFFFF0] flex items-center justify-center">
    <div className="text-center">
      <div className="w-16 h-16 border-4 border-[#C9A689] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-[#4A154B] font-serif text-xl">Loading...</p>
    </div>
  </div>
);

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Landing />} />
      <Route path="/auth" element={<CustomerAuth />} />
      <Route path="/auth/verify" element={<VerifyAuth />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      
      {/* Profile Completion Route (requires auth but not completed profile) */}
      <Route path="/complete-profile" element={<ProfileCompletionRoute><CompleteProfile /></ProfileCompletionRoute>} />
      
      {/* Customer Routes */}
      <Route path="/customer" element={<CustomerRoute><CustomerLayout /></CustomerRoute>}>
        <Route index element={<CustomerDashboard />} />
        <Route path="dashboard" element={<CustomerDashboard />} />
        <Route path="services" element={<CustomerServices />} />
        <Route path="rewards" element={<CustomerRewards />} />
        <Route path="profile" element={<CustomerProfile />} />
        <Route path="history" element={<CustomerHistory />} />
      </Route>
      
      {/* Staff Routes */}
      <Route path="/staff/login" element={<StaffLogin />} />
      <Route path="/staff" element={<StaffRoute><StaffLayout /></StaffRoute>}>
        <Route index element={<StaffDashboard />} />
        <Route path="dashboard" element={<StaffDashboard />} />
        <Route path="scanner" element={<StaffScanner />} />
        <Route path="customers" element={<StaffCustomers />} />
      </Route>
      
      {/* Admin Routes */}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
        <Route index element={<AdminDashboard />} />
        <Route path="dashboard" element={<AdminDashboard />} />
        <Route path="scanner" element={<AdminScanner />} />
        <Route path="customers" element={<AdminCustomers />} />
        <Route path="services" element={<AdminServices />} />
        <Route path="rewards" element={<AdminRewards />} />
        <Route path="promotions" element={<AdminPromotions />} />
        <Route path="staff" element={<AdminStaff />} />
        <Route path="analytics" element={<AdminAnalytics />} />
        <Route path="notifications" element={<AdminNotifications />} />
      </Route>
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster 
          position="top-center" 
          richColors 
          toastOptions={{
            style: {
              background: 'white',
              border: '1px solid #F5E6D3',
            },
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
